import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Users, MapPin, RefreshCw } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  apiCallGetDashboardGraph,
  apiCallGetOverallSummary,
  apiCallGetVenueListOfOwner,
  apiCallGetOverAllSummeryForVenueOwner,
  apiCallGetDashboardGraphForVenueOwner
} from "../../Core/Apicall";
import { useSelector } from "react-redux";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface ProfileData {
  userId?: string;
  role?: number;
}

type FilterType = "today" | "yesterday" | "last7days" | "lastMonth";

const StatCard = ({ icon, label, value, onClick }: any) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      minWidth: 220,
      display: "flex",
      gap: 2,
      p: 2.2,
      borderRadius: 3,
      bgcolor: "#F9FAFB",
      alignItems: "center",
    }}
  >
    <Box
      sx={{ p: 1.4, borderRadius: "50%", bgcolor: "#EEF2FF", display: "flex" }}
    >
      {icon}
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>("today");
  const [totals, setTotals] = useState({
    totalUsers: 0,
    totalVenues: 0,
    totalCheckIns: 0,
  });
  const [totalUsers, setTotalUsers] = useState(0);
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [totalVenues, setTotalVenues] = useState(0);
  const [totalCheckIn, setTotalCheckIns] = useState(0);
  const [ownerVenueList, setOwnerVenueList] = useState<any[]>([]);
  const [selectedVenue, setSelectedVenue] = useState<string>("all");
  const [selectedVenueForGraph, setSelectedVenueForGraph] = useState<string>("all");
  const isProfileData = useSelector((state: any) => state?.global?.profileData) as ProfileData | null;

  useEffect(() => {
    const init = async () => {
      if (isProfileData?.role === 5) {
        await fetchOwnerVenueList();
      } else {
        await Promise.all([
          fetchOverallSummary(),
          fetchGetDashBoardGraph(),
        ]);
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (isProfileData?.role === 5) {
      fetchOverallSummary();
    }
  }, [selectedVenue]);

  useEffect(() => {
    if (isProfileData?.role === 5) {
      fetchGetDashBoardGraph();
    }
  }, [selectedVenueForGraph]);

  useEffect(() => {
    fetchGetDashBoardGraph();
  }, [filter]);


  const fetchOwnerVenueList = async () => {
    try {
      setLoading(true);
      const res = await apiCallGetVenueListOfOwner();

      const venues = res.data || [];
      setOwnerVenueList(venues);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverallSummary = async () => {
    try {
      setLoading(true);

      let res;

      if (isProfileData?.role === 5) {
        const data = { venueId: selectedVenue };
        res = await apiCallGetOverAllSummeryForVenueOwner(data);

        setTotalVenues(res.data.totalVenues);
        setTotalCheckIns(res.data.totalCheckIns);
      } else {
        res = await apiCallGetOverallSummary({});

        setTotalUsers(res.data.totalUsers);
        setTotalVenues(res.data.totalVenues);
        setTotalCheckIns(res.data.totalCheckIns);
      }
    } finally {
      setLoading(false);
    }
  };
  const fetchGetDashBoardGraph = async () => {
    try {
      setLoading(true);

      let res;

      if (isProfileData?.role === 5) {
        const data = {
          filter,
          venueId: selectedVenueForGraph,
        };

        res = await apiCallGetDashboardGraphForVenueOwner(data);
      } else {
        res = await apiCallGetDashboardGraph({
          filter,
        });
      }

      let chartData = res.data.chartData;

      if (isProfileData?.role === 5) {
        chartData = {
          ...chartData,
          datasets: chartData.datasets.filter(
            (item: any) => item.label !== "New Users Joined"
          ),
        };
      }

      setChartData(chartData);

      setTotals({
        totalUsers: res.data.totalUsers || 0,
        totalVenues: res.data.totalVenues || 0,
        totalCheckIns: res.data.totalCheckIns || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshData = () => {
    fetchOwnerVenueList();
    fetchOverallSummary();
    fetchGetDashBoardGraph();
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Box
          component="button"
          onClick={() => refreshData()}
          sx={{
            bgcolor: "#2563EB",
            color: "#fff",
            px: 3,
            py: 1.2,
            borderRadius: 3,
            border: "none",
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <RefreshCw size={16} /> {loading ? "Loading..." : "Refresh"}
        </Box>
      </Box>

      <Card sx={{ p: 3.5, mb: 4, borderRadius: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h5" fontWeight={700}>
            Overall Summary
          </Typography>

          {isProfileData?.role === 5 && (
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Venue List</InputLabel>
              <Select
                value={selectedVenue}
                label="Venue List"
                onChange={(e) => setSelectedVenue(e.target.value)}
              >
                <MenuItem value="all">
                  All Venues
                </MenuItem>

                {ownerVenueList.map((item: any) => (
                  <MenuItem
                    key={item.venue._id}
                    value={item.venue._id}
                  >
                    {item.venue.venueName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box display="flex" gap={2.5} flexWrap="wrap">
          {isProfileData?.role == 2 && <StatCard
            icon={<Users color="#16A34A" />}
            label="Total Users"
            value={totalUsers}
            onClick={() => navigate("/admin/User")}
          />
          }
          <StatCard
            icon={<MapPin color="#EA580C" />}
            label="Total Venues"
            value={totalVenues}
            onClick={() => {
              if (isProfileData?.role === 5) {
                navigate("/admin/venue-owner")
              } else {
                navigate("/admin/Venues")
              }
            }
            }
          />
          <StatCard
            icon={<CheckCircle color="#2563EB" />}
            label="Total CheckIns"
            value={totalCheckIn}
            onClick={() => {
              if (isProfileData?.role === 5) {
                navigate("/admin/venue-owner")
              } else {
                navigate("/admin/Venues")
              }
            }
            }
          />
        </Box>
      </Card>

      <Card sx={{ p: 3.5, borderRadius: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2.5}
        >
          <Typography variant="h5" fontWeight={700}>
            Overview
          </Typography>

          <Box display="flex" gap={2}>
            {isProfileData?.role === 5 && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Venue List</InputLabel>

                <Select
                  value={selectedVenueForGraph}
                  label="Venue List"
                  onChange={(e) => setSelectedVenueForGraph(e.target.value)}
                >
                  <MenuItem value="all">All Venues</MenuItem>

                  {ownerVenueList.map((item: any) => (
                    <MenuItem
                      key={item.venue._id}
                      value={item.venue._id}
                    >
                      {item.venue.venueName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Filter</InputLabel>

              <Select
                value={filter}
                label="Filter"
                onChange={(e) => setFilter(e.target.value as FilterType)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="lastMonth">Last Month</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box display="flex" gap={2.5} flexWrap="wrap" mb={2}>
          {isProfileData?.role == 2 && <StatCard
            icon={<Users color="#16A34A" />}
            label="New Users Joined"
            value={totals.totalUsers}
            onClick={() => navigate("/admin/User")}
          />
          }
          <StatCard
            icon={<MapPin color="#EA580C" />}
            label="Total Venues"
            value={totals.totalVenues}
            onClick={() => {
              if (isProfileData?.role === 5) {
                navigate("/admin/venue-owner")
              } else {
                navigate("/admin/Venues")
              }
            }
            }
          />
          <StatCard
            icon={<CheckCircle color="#2563EB" />}
            label="Check-Ins"
            value={totals.totalCheckIns}
            onClick={() => {
              if (isProfileData?.role === 5) {
                navigate("/admin/venue-owner")
              } else {
                navigate("/admin/Venues")
              }
            }
            }
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{ height: 340, minWidth: filter === "lastMonth" ? 1000 : "100%" }}
        >
          {chartData ? (
            <Line
              data={chartData}
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { position: "top" } },
                scales: { y: { beginAtZero: true } },
              }}
            />
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", pt: 12 }}
            >
              Loading chart...
            </Typography>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default Dashboard;
